import closeIcon from '@raison/static/icons/close-line.svg';
import eyeOn from '@raison/static/icons/eye-line-2.svg';
import eyeOff from '@raison/static/icons/eye-off-line-2.svg';
import searchIcon from '@raison/static/icons/search-line.svg';
import React, {useMemo, useState} from 'react';
import {useComposedRef} from '../../Hooks';
import {RsnInputProps} from './RsnInput.types';

export interface InputPlugin<T> {
    beforeOnChange: (value: string, event: {target: HTMLInputElement}) => T;
    renderValue: (value: T) => string;
}

export interface RsnInputPropsWithPlugin<T> extends Omit<RsnInputProps, 'value' | 'onChange'> {
    plugin: InputPlugin<T>;
    onChange?: (value: T) => void;
    value?: T;
}

export const useRsnInputPlugin = <T,>({
    value,
    onChange,
    plugin,
    ...props
}: RsnInputPropsWithPlugin<T>): RsnInputProps => {
    return {
        ...props,
        value: value && plugin.renderValue(value),
        onChange: onChange && (event => onChange(plugin.beforeOnChange(event.target.value, event))),
    };
};

export const usePassword = ({
    value: externalValue,
    onChange: externalOnChange,
    disabled,
    ...props
}: RsnInputProps): RsnInputProps => {
    const [isVisible, setIsVisible] = useState(false);
    const src = isVisible ? eyeOff : eyeOn;
    const type = isVisible ? 'text' : 'password';

    const [value, onChange] = useUncontrolledInput(externalValue, externalOnChange);

    const isEmpty = value === '';
    const decoration = useMemo(() => {
        if (isEmpty) return undefined;
        return (
            <img
                src={src}
                width='100%'
                height='100%'
                role='button'
                onPointerDown={e => {
                    // prevents focus shift from input
                    e.preventDefault();
                    if (!disabled) setIsVisible(it => !it);
                }}
            />
        );
    }, [src, isEmpty, disabled]);

    return {...props, decoration, value, onChange, disabled, type};
};

const useUncontrolledInput = (
    externalValue: string | undefined,
    externalOnChange?: React.ChangeEventHandler<HTMLInputElement>
): [string, React.ChangeEventHandler<HTMLInputElement>?] => {
    const [internalValue, setInternalValue] = useState('');
    if (externalValue !== undefined) {
        return [externalValue, externalOnChange];
    }
    return [
        internalValue,
        event => {
            setInternalValue(event.target.value);
            externalOnChange?.(event);
        },
    ];
};

const {getOwnPropertyDescriptor} = Object;
const {prototype: HTMLInputPrototype} = HTMLInputElement;
const nativeValueSetter = getOwnPropertyDescriptor(HTMLInputPrototype, 'value')!.set!;

export function useSearch({
    value: externalValue,
    onChange: externalOnChange,
    inputRef: externalInputRef,
    ...props
}: RsnInputProps): RsnInputProps {
    const [value, onChange] = useUncontrolledInput(externalValue, externalOnChange);
    const inputRef = useComposedRef(externalInputRef);
    const decoration = useMemo(() => {
        if (value) {
            return (
                <img
                    src={closeIcon}
                    width='100%'
                    height='100%'
                    onClick={() => {
                        if (inputRef.current) {
                            nativeValueSetter.call(inputRef.current, '');
                            inputRef.current.dispatchEvent(new Event('input', {bubbles: true}));
                        }
                    }}
                    onPointerDown={e => e.preventDefault()}
                />
            );
        } else {
            return <img src={searchIcon} width='100%' height='100%' />;
        }
    }, [value]);

    return {...props, value, onChange, type: 'search', decoration, inputRef};
}
